import React, { useEffect } from 'react';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';

const IntakeQuestions = ({
  sections,
  currentSection,
  currentQuestion,
  answers,
  onAnswer,
  onNext,
  onBack,
}) => {
  const sectionData = sections[currentSection];
  const questionData = sectionData?.questions[currentQuestion];

  const totalQuestions = sections.reduce((acc, s) => acc + s.questions.length, 0);
  const questionsAnswered = sections
    .slice(0, currentSection)
    .reduce((acc, s) => acc + s.questions.length, 0) + currentQuestion;
  const progressPercentage = (questionsAnswered / totalQuestions) * 100;

  const isAnswered = (() => {
    if (!questionData) return false;
    const a = answers[questionData.id];
    if (questionData.type === 'multi-select') return a && a.length > 0;
    return !!a;
  })();

  const isLast =
    currentSection === sections.length - 1 &&
    currentQuestion === sectionData.questions.length - 1;

  // Keyboard: Enter advances when an answer is selected (UX polish)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Enter' && isAnswered) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isAnswered, onNext]);

  if (!questionData) return null;

  return (
    <div className="assessment-questions">
      <div className="assessment-progress">
        <div className="assessment-progress-header">
          <span className="assessment-section-label">{sectionData.title}</span>
          <span className="assessment-step-label">
            Section {currentSection + 1} of {sections.length}
          </span>
        </div>
        <div className="assessment-progress-bar">
          <div
            className="assessment-progress-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      <div className="assessment-question-container">
        {sectionData.introText && (
          <p className="assessment-section-intro">{sectionData.introText}</p>
        )}
        <h2 className="assessment-question-text">{questionData.question}</h2>
        {questionData.subtext && (
          <p className="assessment-question-subtext">{questionData.subtext}</p>
        )}

        <div className="assessment-options">
          {questionData.options.map((option) => {
            const isMulti = questionData.type === 'multi-select';
            const isSelected = isMulti
              ? (answers[questionData.id] || []).includes(option.value)
              : answers[questionData.id] === option.value;

            return (
              <button
                key={option.value}
                className={`assessment-option ${isSelected ? 'selected' : ''}`}
                onClick={() => onAnswer(questionData.id, option.value, isMulti)}
                data-testid={`option-${option.value}`}
              >
                <span className="assessment-option-indicator">
                  {isMulti ? (
                    <span className={`checkbox ${isSelected ? 'checked' : ''}`}>
                      {isSelected && <Check size={14} />}
                    </span>
                  ) : (
                    <span className={`radio ${isSelected ? 'checked' : ''}`} />
                  )}
                </span>
                <span className="assessment-option-label">{option.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="assessment-navigation">
        <button
          className="btn-assessment-back"
          onClick={onBack}
          data-testid="intake-back-btn"
        >
          <ArrowLeft size={18} />
          Back
        </button>
        <button
          className="btn-assessment-next"
          onClick={onNext}
          disabled={!isAnswered}
          data-testid="next-btn"
        >
          {isLast ? 'See Results' : 'Continue'}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default IntakeQuestions;
